import { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useSpring,
} from "framer-motion";
import {
  Search,
  Users,
  PenTool,
  Film,
  Rocket,
  BarChart3,
  Check,
} from "lucide-react";

const steps = [
  {
    n: "01",
    title: "Understand",
    desc: "Map goals, audience, regional context.",
    icon: Search,
    tag: "Discovery",
  },
  {
    n: "02",
    title: "Identify",
    desc: "Match creators by voice, not followers.",
    icon: Users,
    tag: "Casting",
  },
  {
    n: "03",
    title: "Co-create",
    desc: "Scripts and concepts written with creators.",
    icon: PenTool,
    tag: "Creation",
  },
  {
    n: "04",
    title: "Narrative",
    desc: "Arcs audiences follow episodically.",
    icon: Film,
    tag: "Story",
  },
  {
    n: "05",
    title: "Execute",
    desc: "Multi-creator waves, on platform, on time.",
    icon: Rocket,
    tag: "Launch",
  },
  {
    n: "06",
    title: "Optimize",
    desc: "Weekly reads. Iterate mid-flight.",
    icon: BarChart3,
    tag: "Scale",
  },
];

const CARD_WIDTH = 320;
const GAP = 28;
const ROW_TRAVEL = (steps.length - 1) * (CARD_WIDTH + GAP);

function StepCard({ step, index, state }) {
  const Icon = step.icon;
  const isActive = state === "active";
  const isCompleted = state === "completed";

  return (
    <motion.div
      animate={{
        y: isActive ? -6 : 0,
        opacity: isActive ? 1 : isCompleted ? 0.9 : 0.5,
      }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`process-step-${index + 1}`}
      className="relative shrink-0"
      style={{ width: CARD_WIDTH }}
    >
      <div
        className={[
          "relative rounded-2xl p-6 h-full overflow-hidden bg-white dark:bg-white/[0.03] transition-all duration-500",
          isActive
            ? "border border-[#2563EB]/60 shadow-[0_25px_60px_-25px_rgba(37,99,235,0.5)]"
            : isCompleted
              ? "border border-blue-200/70 dark:border-blue-400/25 bg-blue-50/40 dark:bg-blue-500/[0.05]"
              : "border border-slate-200 dark:border-white/10",
        ].join(" ")}
      >
        <span
          aria-hidden
          className="absolute top-4 right-5 font-heading font-black text-[34px] leading-none tracking-tight text-slate-100 dark:text-white/[0.06] select-none pointer-events-none"
        >
          {step.n}
        </span>

        <div className="relative flex items-center gap-3">
          <motion.div
            animate={{ scale: isActive ? 1.05 : 1 }}
            transition={{ duration: 0.35 }}
            className={[
              "w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-500",
              isActive || isCompleted
                ? "bg-[#2563EB] text-white shadow-[0_8px_20px_-8px_rgba(37,99,235,0.6)]"
                : "bg-slate-100 dark:bg-white/[0.05] text-slate-500 dark:text-slate-400",
            ].join(" ")}
          >
            <Icon className="w-4 h-4" />
          </motion.div>
          <span
            className={[
              "text-[10.5px] tracking-[0.22em] uppercase font-semibold transition-colors",
              isActive
                ? "text-[#2563EB] dark:text-blue-300"
                : "text-slate-400 dark:text-slate-500",
            ].join(" ")}
          >
            {step.tag}
          </span>
        </div>

        <h3
          className={[
            "relative mt-5 font-heading font-bold text-[22px] leading-tight tracking-tight transition-colors duration-500",
            isActive
              ? "text-slate-900 dark:text-white"
              : "text-slate-700 dark:text-slate-200",
          ].join(" ")}
        >
          {step.title}
        </h3>
        <p className="relative mt-2 text-[13.5px] text-slate-600 dark:text-slate-300 leading-relaxed">
          {step.desc}
        </p>

        <div
          className={[
            "relative mt-6 pt-4 border-t flex items-center justify-between transition-colors duration-500",
            isActive
              ? "border-[#2563EB]/30"
              : "border-slate-100 dark:border-white/10",
          ].join(" ")}
        >
          <span className="text-[10.5px] tracking-[0.2em] uppercase font-semibold text-slate-400 dark:text-slate-500">
            Step {step.n}
          </span>
          <motion.div
            animate={{ width: isActive ? 48 : 24 }}
            transition={{ duration: 0.4 }}
            className={[
              "h-[2px] rounded-full transition-colors duration-500",
              isActive || isCompleted
                ? "bg-gradient-to-r from-[#3B82F6] to-[#2563EB]"
                : "bg-slate-200 dark:bg-white/10",
            ].join(" ")}
          />
        </div>
      </div>
    </motion.div>
  );
}

function Node({ state }) {
  const isActive = state === "active";
  const isCompleted = state === "completed";
  return (
    <span className="relative flex items-center justify-center w-5 h-5">
      {isActive && (
        <span
          aria-hidden
          className="absolute inset-[-8px] rounded-full bg-[#2563EB]/30 blur-md anim-pulse-glow"
        />
      )}
      <span
        className={[
          "relative w-3 h-3 rounded-full transition-all duration-400 flex items-center justify-center",
          isActive
            ? "bg-[#2563EB] ring-[3px] ring-[#2563EB]/25"
            : isCompleted
              ? "bg-[#2563EB]"
              : "bg-white dark:bg-[#0B1C3D] border-2 border-slate-300 dark:border-white/20",
        ].join(" ")}
      >
        {isCompleted && (
          <Check className="w-2 h-2 text-white" strokeWidth={3.5} />
        )}
      </span>
    </span>
  );
}

function HorizontalTimeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Smooth the raw scroll progress with a spring — eliminates jitter from
  // small wheel events and gives the entire animation a gliding feel.
  const animProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 28,
    mass: 0.5,
    restDelta: 0.0008,
  });

  const [active, setActive] = useState(0);
  useMotionValueEvent(animProgress, "change", (v) => {
    const clamped = Math.min(0.9999, Math.max(0, v));
    const idx = Math.floor(clamped * steps.length);
    if (idx !== active) setActive(idx);
  });

  // 26vw inset on each side → ~200px from viewport edge to card outer edge,
  // roughly 2× the previous 18vw clearance the user requested.
  // tx(0) = -160px (card 1 centered at 26vw, half-width = 160)
  // tx(1) = 48vw - 1900px (card 6 centered at viewport - 26vw)
  const x = useTransform(
    animProgress,
    (p) => `calc(${48 * p}vw - ${(1740 * p + 160).toFixed(1)}px)`
  );
  const fillPercent = useTransform(animProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="process"
      ref={ref}
      data-testid="process-section"
      className="relative surface-1 hidden lg:block"
      style={{ height: "300vh" }}
    >
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col">
        {/* faint grid bg */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] dark:opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(30,58,138,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,138,0.7) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          }}
        />

        {/* Header */}
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-10 pt-20 md:pt-24">
          <div className="flex items-end justify-between gap-5">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 px-3 py-1 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] anim-pulse-glow" />
                <span className="eyebrow text-[#2563EB] dark:text-blue-300">
                  Process · 06 Steps
                </span>
              </div>
              <h2 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.05] text-slate-900 dark:text-white">
                How we build{" "}
                <span className="gradient-text">campaigns that work.</span>
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <p className="hidden md:block text-[13px] text-slate-500 dark:text-slate-400 max-w-[260px] leading-snug">
                Scroll to walk through the operating system.
              </p>
              <div
                data-testid="process-counter"
                className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 px-3 py-1.5 shadow-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                <span className="font-mono text-[12px] tracking-wider text-slate-700 dark:text-slate-200">
                  {String(active + 1).padStart(2, "0")}
                  <span className="text-slate-400 dark:text-slate-500">
                    {" "}
                    / {String(steps.length).padStart(2, "0")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Track — cards + line sit together naturally */}
        <div className="relative pt-10 md:pt-14 pb-10">
          {/* soft glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[55%] -translate-y-1/2 w-[55vw] h-48 rounded-full blur-3xl opacity-50 dark:opacity-40"
            style={{
              background:
                "radial-gradient(ellipse, rgba(37,99,235,0.25) 0%, transparent 70%)",
            }}
          />

          {/* Translating cards row — only cards move */}
          <div className="relative overflow-hidden">
            <motion.div
              style={{ x }}
              className="flex items-end pl-[26vw] pr-[26vw] will-change-transform"
            >
              {steps.map((s, i) => (
                <div
                  key={s.n}
                  style={{ marginRight: i < steps.length - 1 ? GAP : 0 }}
                >
                  <StepCard
                    step={s}
                    index={i}
                    state={
                      i === active
                        ? "active"
                        : i < active
                          ? "completed"
                          : "inactive"
                    }
                  />
                </div>
              ))}
            </motion.div>
          </div>

          {/* === STATIC TIMELINE — sits directly under the cards === */}
          <div className="relative mt-8 md:mt-10 mx-[26vw]">
            {/* Base line — sharp, thin, with faded edges */}
            <div
              className="relative h-[2px] bg-slate-200 dark:bg-white/[0.08]"
              style={{
                maskImage:
                  "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
              }}
            >
              {/* Progress fill — only width animates, edges fade with line */}
              <motion.div
                style={{ width: fillPercent }}
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#3B82F6]"
              />
            </div>

            {/* Nodes — evenly distributed across the line */}
            <div className="absolute -top-[9px] left-0 right-0 flex justify-between">
              {steps.map((s, i) => (
                <Node
                  key={s.n}
                  state={
                    i === active
                      ? "active"
                      : i < active
                        ? "completed"
                        : "inactive"
                  }
                />
              ))}
            </div>

            {/* Step labels under nodes */}
            <div className="absolute top-5 left-0 right-0 flex justify-between">
              {steps.map((s, i) => (
                <div
                  key={s.n}
                  className="flex flex-col items-center w-0"
                  style={{ minWidth: 0 }}
                >
                  <span
                    className={[
                      "font-mono text-[10.5px] tracking-wider whitespace-nowrap transition-colors duration-400",
                      i === active
                        ? "text-[#2563EB] dark:text-blue-300"
                        : i < active
                          ? "text-slate-500 dark:text-slate-400"
                          : "text-slate-300 dark:text-slate-600",
                    ].join(" ")}
                  >
                    {s.n}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VerticalTimeline() {
  return (
    <section
      data-testid="process-section-mobile"
      className="relative py-20 surface-1 lg:hidden"
    >
      <div className="mx-auto max-w-3xl px-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 px-3 py-1 mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] anim-pulse-glow" />
          <span className="eyebrow text-[#2563EB] dark:text-blue-300">
            Process · 06 Steps
          </span>
        </div>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight leading-[1.08] text-slate-900 dark:text-white">
          How we build{" "}
          <span className="gradient-text">campaigns that work.</span>
        </h2>

        <div className="relative mt-12 pl-8">
          <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-slate-200 dark:bg-white/10" />

          <ol className="space-y-6">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="relative"
                >
                  <span
                    aria-hidden
                    className="absolute -left-[29px] top-3 w-4 h-4 rounded-full bg-white dark:bg-[#0B1C3D] border-2 border-[#2563EB] flex items-center justify-center"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                  </span>
                  <div className="rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#2563EB] text-white flex items-center justify-center">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#2563EB] dark:text-blue-300">
                        {s.tag}
                      </span>
                      <span className="ml-auto text-[10.5px] tracking-[0.2em] uppercase font-semibold text-slate-400 dark:text-slate-500">
                        Step {s.n}
                      </span>
                    </div>
                    <h3 className="mt-3 font-heading font-bold text-xl text-slate-900 dark:text-white tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-1 text-[13.5px] text-slate-600 dark:text-slate-300 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

export default function Process() {
  return (
    <>
      <HorizontalTimeline />
      <VerticalTimeline />
    </>
  );
}
